'use client';

import { useAuth } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useState } from 'react';
import { AnimatedButton, type ButtonSize } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { GiftIcon } from '@/components/ui/icon/glyphs';
import { checkGate } from '@/lib/analytics';
import { SendGiftModal } from './send-gift-modal';

/** Keep in sync with `iconSizeClasses` on {@link AnimatedButton}. */
const GIFT_ICON_CLASS_BY_SIZE: Record<ButtonSize, string> = {
  xs: 'size-icon-sm',
  sm: 'size-icon-md',
  default: 'size-icon-md',
  lg: 'size-icon-lg',
  xl: 'size-icon-lg',
};

export interface GiftButtonProps {
  /** Recipient platform — defaults to Morfi when handle is set. */
  recipientPlatform?: 'morfi' | 'x';
  /** Pre-fill the modal's handle field. */
  recipientHandle?: string | null;
  /** Recipient user id (for self-gift detection on the server). */
  recipientUserId?: string | null;
  /** Visual size — matches AnimatedButton size token. */
  size?: ButtonSize;
  /** Visual variant — defaults to secondary. */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'destructive';
  /** Optional className passthrough. */
  className?: string;
  /**
   * Fires synchronously when the button is pressed, before the modal opens.
   * Use this to seed dependent state (e.g. the active-wallet store) so the
   * modal's internal wallet picker defaults correctly.
   */
  onClickBefore?: () => void;
}

/**
 * Entry-point button for the Send-Gift flow (US-1).
 *
 * Visibility rules (FR-001):
 *  - Hidden when the viewer is unauthenticated.
 *  - Hidden when the recipient resolves to the viewer's own user id
 *    (self-gift prevention; defence in depth — server also rejects).
 *
 * The recipient handle/platform are passed through to {@link SendGiftModal}
 * so the modal opens pre-filled.
 */
export function GiftButton(props: GiftButtonProps): JSX.Element | null {
  const t = useTranslations('gift');
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);

  const isSelf =
    props.recipientUserId !== undefined &&
    props.recipientUserId !== null &&
    user !== null &&
    props.recipientUserId === user.id;

  // Gifting kill-switch — hides every Send-Gift entry point when OFF.
  if (!checkGate('gifting') || !isAuthenticated || isSelf) {
    return null;
  }

  const resolvedSize = props.size ?? 'default';

  return (
    <>
      <AnimatedButton
        variant={props.variant ?? 'secondary'}
        size={resolvedSize}
        onClick={(): void => {
          props.onClickBefore?.();
          setOpen(true);
        }}
        aria-label={
          props.recipientHandle !== null && props.recipientHandle !== undefined
            ? t('buttonAria', { handle: `@${props.recipientHandle}` })
            : t('button')
        }
        className={props.className}
      >
        <Icon
          icon={GiftIcon}
          className={GIFT_ICON_CLASS_BY_SIZE[resolvedSize]}
          aria-hidden="true"
        />
        {t('button')}
      </AnimatedButton>
      {open ? (
        <SendGiftModal
          open={open}
          onOpenChange={setOpen}
          initialPlatform={props.recipientPlatform ?? 'morfi'}
          initialHandle={props.recipientHandle ?? ''}
        />
      ) : null}
    </>
  );
}
